import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { X, ArrowRight, MapPin, Sparkles } from 'lucide-react';

/** Route → intro copy. New entries here automatically show their card the
 *  first time the user visits that route (until they hit skip / the global
 *  limits kick in). Copy is one paragraph + a short bullet list. */
const SCREEN_INTROS = {
  '/home': {
    title: 'Home',
    body: 'Your daily briefing — active projects, unread alerts, and open work orders at a glance.',
    bullets: ['Cards jump into their sections when you tap them.', 'Search or open any tool with ⌘K / Ctrl-K.'],
  },
  '/my-week': {
    title: 'My Week',
    body: 'Everything on your plate this week — commitments, milestones you own, and slip trends.',
    bullets: ['Mark items Done or Shift them straight from a card.', 'Add a new commitment via the + button.'],
  },
  '/commitments': {
    title: 'Commitments',
    body: 'Public promises you make to the team. Delivered or overdue is visible to everyone.',
    bullets: ['Every commitment has a target date and priority.', 'Shifting a date requires a reason.'],
  },
  '/master-projects': {
    title: 'Master Project Data',
    body: 'The single source of truth for every project — stage, dates, amounts, team, and milestones.',
    bullets: ['Owner/Leads can add projects directly.', 'Link a Google Sheet to keep both sides in sync.'],
  },
  '/team': {
    title: 'Team Directory',
    body: 'Who is who, and who owns what. Manage the roster from here.',
    bullets: ['Owner/Leads can add or edit teammates.', 'Link a separate Google Sheet for HR-friendly editing.'],
  },
  '/knowledge': {
    title: 'Knowledge Hub',
    body: 'The studio\u2019s shared library — SOPs, email templates, and design resources.',
    bullets: [
      'SOPs are role-split into Design and Accounts folders.',
      'Anyone can recommend an SOP; the Owner approves it live.',
      'Email templates need one Ops/Owner approval before going public.',
    ],
  },
  '/reports': {
    title: 'Reports',
    body: 'Owner-level insights — project health, financial summary, milestone tracker, and team workload.',
    bullets: ['Charts filter by client and stage.', 'Every table has an Excel + PDF export.'],
  },
  '/projects': {
    title: 'Projects',
    body: 'Every project the studio is currently running.',
    bullets: ['Click a card for the full board with milestones and files.'],
  },
  '/work-orders': {
    title: 'Work Orders',
    body: 'Uploaded WOs live here. Ops and Owner verify them, then they flow into the master list.',
    bullets: ['Drag-drop a PDF to start intake.', 'AI parses the fields — always double-check.'],
  },
  '/travel': {
    title: 'Travel & Visits',
    body: 'Site visits, client travel, and trip claims — all in one place.',
    bullets: ['Add a trip before you go, close it out when you\u2019re back.'],
  },
  '/timesheets': {
    title: 'Timesheets',
    body: 'Log hours by project and stage. HR uses this to see who\u2019s stretched.',
    bullets: ['Save often; drafts stay put.'],
  },
  '/quotes': {
    title: 'Quote Register',
    body: 'Live pipeline of quotes going out. Owner and Accounts track win rate here.',
    bullets: ['Every quote has an id, client, and status.'],
  },
  '/pending-invoices': {
    title: 'Pending Invoices',
    body: 'Invoices raised but not yet paid. Accounts drives this queue.',
    bullets: ['Aging over 30 days flags red.'],
  },
  '/admin/users': {
    title: 'Admin · Users',
    body: 'The full teammate table, including pending sign-ups awaiting your approval.',
    bullets: ['Change a teammate\u2019s role or status inline.'],
  },
  '/admin/feedback': {
    title: 'Admin · Feedback',
    body: 'Every note the team leaves via the floating Feedback button. Cluster by kind to spot V3 themes.',
    bullets: ['Mark items Reviewed / Resolved / Archived to keep the queue tidy.'],
  },
  '/admin/settings': {
    title: 'Admin · Settings',
    body: 'Studio-wide switches — domain rules, notification cadences, brand touches.',
    bullets: ['Owner-only.'],
  },
  '/admin/holidays': {
    title: 'Admin · Holidays',
    body: 'Fixed and floating holidays feed the working-day math on My Week and reports.',
    bullets: ['Bulk import via the .ics upload.'],
  },
  '/admin/audit': {
    title: 'Admin · Audit',
    body: 'Immutable log of every mutating action in the app — who did what and when.',
    bullets: ['Filter by user, action, or entity.'],
  },
  '/how-to': {
    title: 'How-To',
    body: 'Plain-English guides for every part of the app, personalised to your role.',
    bullets: ['These update automatically when permissions change.'],
  },
  '/release-manager': {
    title: 'Release Manager',
    body: 'Toggle features on/off per release phase. Owner-only.',
    bullets: ['Rollouts are timed — no big-bang launches.'],
  },
};

// Some routes are dynamic (e.g. /projects/:id, /knowledge/:pageId). Match by prefix.
function matchScreenKey(pathname) {
  if (SCREEN_INTROS[pathname]) return pathname;
  if (pathname.startsWith('/projects/')) return '/projects';
  if (pathname.startsWith('/knowledge/')) return '/knowledge';
  if (pathname.startsWith('/work-orders/')) return '/work-orders';
  if (pathname.startsWith('/admin/')) return null; // rely on exact matches only for admin
  return null;
}


/** Bottom-right screen intro overlay. Shows the first time this user lands
 *  on any route defined in `SCREEN_INTROS`. Persistence + limits are enforced
 *  by /api/tour/state + /api/tour/skip on the backend. */
export function TourOverlay() {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [farewell, setFarewell] = useState(false);
  const sessionIdRef = useRef(null);

  // Grab or mint a stable per-tab session id so the backend can enforce the
  // "one skip per login session" rule without a login/logout hook.
  useEffect(() => {
    let sid = sessionStorage.getItem('tour_session_id');
    if (!sid) {
      sid = crypto?.randomUUID?.() || `s${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('tour_session_id', sid);
    }
    sessionIdRef.current = sid;
  }, []);

  // Load the tour state whenever the user or the route changes.
  useEffect(() => {
    if (!user) { setState(null); return; }
    let alive = true;
    api.get('/tour/state').then(r => { if (alive) setState(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [user, location.pathname]);

  useEffect(() => { setDismissed(false); setFarewell(false); }, [location.pathname]);

  // Farewell mode: overrides everything, shown once when the LAST skip stops the tour.
  if (farewell) return <TourFarewellCard onClose={() => setFarewell(false)} />;

  if (!user || !state || !state.should_show || dismissed) return null;

  const key = matchScreenKey(location.pathname);
  if (!key) return null;
  const intro = SCREEN_INTROS[key];
  if (!intro) return null;

  const alreadySeen = (state.seen_screens || []).includes(key);
  if (alreadySeen) return null;

  const markSeen = () => {
    api.post('/tour/screen-seen', { screen: key }).catch(() => {});
    setState(s => s ? { ...s, seen_screens: [...(s.seen_screens || []), key] } : s);
  };

  const gotIt = () => {
    markSeen();
    setDismissed(true);
  };

  const skip = async () => {
    try {
      const r = await api.post('/tour/skip', { session_id: sessionIdRef.current, screen: key });
      setState(r.data);
      // If this skip pushed the tour over its limits, celebrate the goodbye.
      if (!r.data.should_show && r.data.stop_reason) {
        setFarewell(true);
        return;
      }
    } catch { /* soft-fail — dismiss anyway */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-40 w-[240px] max-w-[calc(100vw-2rem)] rounded-lg overflow-hidden
                 shadow-[0_10px_24px_-10px_hsl(var(--foreground)/.35)]
                 bg-[hsl(var(--surface-1))] border border-[hsl(var(--primary)/.28)]
                 transition-transform duration-300 ease-out will-change-transform
                 hover:translate-y-[-1px] tour-card-signature"
      role="dialog"
      aria-label={`Introduction to ${intro.title}`}
      data-testid={`tour-card-${key.replace(/\//g, '-')}`}
    >
      {/* Brand accent stripe on the left edge */}
      <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-[hsl(var(--primary))] via-[hsl(var(--primary)/.6)] to-[hsl(var(--primary)/.15)]"
           aria-hidden="true" />
      <div className="px-2.5 py-1.5 flex items-center gap-1.5 bg-[hsl(var(--primary)/.08)]">
        <MapPin size={11} className="text-[hsl(var(--primary))]" />
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--primary))] truncate flex-1">
          {intro.title}
        </div>
        <button onClick={() => setDismissed(true)} data-testid="tour-close-btn"
                aria-label="Close for now"
                className="w-5 h-5 grid place-items-center rounded hover:bg-[hsl(var(--primary)/.14)] text-[hsl(var(--foreground-muted))]">
          <X size={11} />
        </button>
      </div>
      <div className="px-2.5 py-2 space-y-1.5">
        <div className="text-[12.5px] leading-snug">{intro.body}</div>
        <div className="flex items-center justify-between pt-0.5">
          <button
            onClick={skip}
            data-testid="tour-skip-btn"
            className="text-[10.5px] text-[hsl(var(--foreground-muted))] underline underline-offset-2 hover:text-[hsl(var(--foreground))]"
          >
            Skip ({state.skips_remaining})
          </button>
          <button
            onClick={gotIt}
            data-testid="tour-got-it-btn"
            className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10.5px] font-semibold
                       bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
          >
            Got it <ArrowRight size={9} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Confetti + farewell card shown once when the very last Skip Tour click is fired
 *  (or the 15-day window expires). It's the tour's goodbye — a small studio
 *  flourish that celebrates the user "graduating" out of onboarding. */
function TourFarewellCard({ onClose }) {
  // Auto-dismiss after 6s so it never becomes a nuisance.
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [onClose]);

  const pieces = Array.from({ length: 18 });
  return (
    <div
      className="fixed bottom-16 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] rounded-xl overflow-hidden
                 shadow-[0_20px_48px_-14px_hsl(var(--primary)/.55)]
                 bg-[hsl(var(--surface-1))] border border-[hsl(var(--primary)/.45)]
                 -rotate-[1deg] tour-card-signature tour-farewell-enter"
      role="dialog"
      aria-label="Tour complete"
      data-testid="tour-farewell-card"
    >
      {/* Confetti burst */}
      <div className="tour-confetti pointer-events-none" aria-hidden="true">
        {pieces.map((_, i) => (
          <span key={i} className={`piece p${(i % 6) + 1}`} style={{ '--i': i }} />
        ))}
      </div>
      <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-[hsl(var(--primary))] via-[hsl(var(--primary)/.6)] to-[hsl(var(--primary)/.15)]" />
      <div className="px-3.5 py-2.5 flex items-center gap-2 bg-[hsl(var(--primary)/.10)]">
        <Sparkles size={14} className="text-[hsl(var(--primary))]" />
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[hsl(var(--primary))]">
          You&apos;re all set
        </div>
        <button onClick={onClose} data-testid="tour-farewell-close"
                aria-label="Dismiss"
                className="ml-auto w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--primary)/.16)] text-[hsl(var(--foreground-muted))]">
          <X size={12} />
        </button>
      </div>
      <div className="p-4 space-y-1.5">
        <div className="text-[15px] font-heading font-semibold leading-tight">That&apos;s it. Enjoy the studio.</div>
        <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
          The screen tour has done its job. Anything you need again lives under
          <span className="text-[hsl(var(--foreground))] font-medium"> How to use</span> in the sidebar.
        </div>
      </div>
    </div>
  );
}
